import styled from '@emotion/styled';
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { Button } from 'components/commons/Button';
import { theme } from 'styles/theme';
import { FlexBox } from 'components/commons/FlexBox';
import { ErrorMessage } from './ErrorMessage';
import { IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ModalAlert } from './ModalAlert';
import { SucessSendMessage } from './SucessSendMessage';
import ErrorIcon from 'components/assets/Icon_error.svg';
import SuccessIcon from 'components/assets/Icon_check.svg';

const Form = styled.form`
  position: relative;
  padding-top: 6rem;
  flex: 1;
  ${{
    [theme.mediaQueries.md]: {
      width: '70%',
      flex: 'unset',
    },
  }}
`;

const FieldSet = styled.fieldset`
  display: flex;
  border: none;
  flex-wrap: wrap;
`;

const FieldGroup = styled(FlexBox)`
  position: relative;
  flex: 1;
  flex-direction: ${({ flexDirection }) => flexDirection || 'column'};
  margin: 1rem 1.5rem;
  ${{
    [theme.mediaQueries.md]: {
      margin: '1.5rem',
    },
  }}
  flex-wrap: wrap;
`;

const Label = styled.label`
  display: block;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
`;

const Input = styled.input<{ error?: boolean; success?: boolean }>`
  background-color: ${({ error, success }) =>
    error
      ? theme.colors.input.error.background
      : success
      ? theme.colors.input.success.background
      : theme.colors.lightBlueGreen};
  border: 3px solid
    ${({ error, success }) =>
      error
        ? theme.colors.input.error.border
        : success
        ? theme.colors.input.success.border
        : 'transparent'};
  transition: all 150ms ease;
  padding: 1rem;
  border-radius: 10px;
  font-size: 1.6rem;
  font-family: ${theme.fontsFamily.mulish.regular};
  outline: none;
  &:focus {
    background-color: transparent;
    border: 3px solid ${theme.colors.blueGreen};
  }
`;

const TextArea = styled.textarea`
  background-color: ${theme.colors.lightBlueGreen};
  border: none;
  padding: 1rem;
  border-radius: 7px;
  resize: none;
  font-family: ${theme.fontsFamily.mulish.regular};
`;

const Icon = styled.div`
  position: absolute;
  height: 20px;
  width: 20px;
  top: 36px;
  right: 10px;
`;

const SubmitButton = styled(Button)`
  border-radius: 7px;
  padding: 0.75rem 1.5rem;
  margin: 1rem 0;
  ${{
    [theme.mediaQueries.md]: {
      backgroundColor: theme.colors.blueGreen,
      hover: theme.colors.orange,
      borderRadius: '10rem',
    },
  }}
`;

const SendMailLink = styled.a`
  display: block;
  text-decoration: underline;
`;
interface FormType {
  lastname: string;
  firstname: string;
  email: string;
  phone: string;
  message: string;
  response: string;
}

export const ContactForm = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('contact');
  const [isSending, setIsSending] = useState(false);

  const defaultFieldsError = useMemo(
    () => ({
      lastname: {
        success: false,
        error: false,
      },
      firstname: {
        success: false,
        error: false,
      },
      email: {
        success: false,
        error: false,
      },
    }),
    []
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, any>>(
    defaultFieldsError
  );
  const rootRef = useRef<any | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    t('form.error.message')
  );

  const defaultState = useMemo(
    () => ({
      lastname: '',
      firstname: '',
      email: '',
      phone: '',
      message: '',
      response: t('form.successMessage.response'),
    }),
    []
  );
  const [form, setForm] = useState<FormType>(defaultState);
  const [successSend, setSuccessSend] = useState(false);

  const fetcher = useCallback(
    (url, data) =>
      fetch(url, { method: 'post', body: data }).then((res) => res.json()),
    []
  );

  const handleInputChange = (event: any) => {
    const { name, value } = event.currentTarget;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSending(true);
    const canSubmit =
      !fieldErrors.lastname.error &&
      !fieldErrors.firstname.error &&
      !fieldErrors.email.error;
    if (!canSubmit) return null;
    try {
      const query = JSON.stringify({
        query: `mutation sendMessage {
          payload: sendMessage(values: {
            lastname: "${form.lastname}",
            firstname: "${form.firstname}",
            email: "${form.email}",
            phone: "${form.phone}",
            message: "${form.message}"
            response: "${form.response}"
          }) {
            message
          }
        }
    `,
      });
      const { errors } = await fetcher('/api/graphql', query);
      if (errors) throw new Error(t('form.error.message'));
      setErrorMessage(null);
      setIsSending(false);
      setSuccessSend(true);
    } catch (error) {
      setErrorMessage(error.message);
      setIsSending(false);
      setOpen(true);
    }
  };

  const handleResetForm = () => {
    setSuccessSend(false);
    setForm(defaultState);
    setFieldErrors(defaultFieldsError);
  };

  const handleOnBlur = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    isRequired
  ) => {
    const { value, name } = event.currentTarget;
    const validateEmail = (mail: string) => {
      if (
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
          mail
        )
      ) {
        return true;
      }
      return false;
    };
    if ((isRequired && !value) || (name === 'email' && !validateEmail(value))) {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [name]: {
          ...prevErrors[name],
          error: true,
          success: false,
        },
      }));
    } else if (value && isRequired) {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [name]: {
          ...prevErrors[name],
          error: false,
          success: true,
        },
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit} ref={rootRef} id="contact-form" noValidate>
      {!isSending && successSend ? (
        <SucessSendMessage onShowForm={handleResetForm} />
      ) : (
        <>
          <FieldSet>
            <FieldGroup flexDirection="column">
              <Label htmlFor="lastname">{t('form.lastname')}*</Label>
              <Input
                id="lastname"
                onChange={handleInputChange}
                name="lastname"
                value={form.lastname}
                required
                onBlur={(event) => handleOnBlur(event, true)}
                error={fieldErrors.lastname.error}
                success={fieldErrors.lastname.success}
              />
              {fieldErrors.lastname.error ? (
                <Icon>
                  <ErrorIcon />
                </Icon>
              ) : fieldErrors.lastname.success ? (
                <Icon>
                  <SuccessIcon />
                </Icon>
              ) : null}
            </FieldGroup>
            <FieldGroup flexDirection="column">
              <Label htmlFor="firstname">{t('form.firstname')}*</Label>
              <Input
                id="firstname"
                onChange={handleInputChange}
                name="firstname"
                value={form.firstname}
                required
                onBlur={(event) => handleOnBlur(event, true)}
              />
              {fieldErrors.firstname.error ? (
                <Icon>
                  <ErrorIcon />
                </Icon>
              ) : fieldErrors.firstname.success ? (
                <Icon>
                  <SuccessIcon />
                </Icon>
              ) : null}
            </FieldGroup>
          </FieldSet>
          <FieldSet>
            <FieldGroup>
              <Label htmlFor="email">{t('form.email')}*</Label>
              <Input
                id="email"
                type="email"
                onChange={handleInputChange}
                name="email"
                value={form.email}
                required
                onBlur={(event) => handleOnBlur(event, true)}
                error={fieldErrors.email.error}
                success={fieldErrors.email.success}
              />
              {fieldErrors.email.error ? (
                <Icon>
                  <ErrorIcon />
                </Icon>
              ) : fieldErrors.email.success ? (
                <Icon>
                  <SuccessIcon />
                </Icon>
              ) : null}
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="phone">{t('form.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                onChange={handleInputChange}
                name="phone"
                value={form.phone}
              />
            </FieldGroup>
          </FieldSet>
          <FieldGroup>
            <Label htmlFor="message">{t('form.message')}</Label>
            <TextArea
              id="message"
              onChange={handleInputChange}
              rows={15}
              name="message"
              value={form.message}
            />
          </FieldGroup>
          <FieldGroup
            alignItems="flex-start"
            flexDirection="row"
            justifyContent="space-between"
          >
            <SubmitButton
              type="submit"
              backgroundColor={theme.colors.blueGreen}
              hover={theme.colors.orange}
              uppercase
              disabled={
                isSending || !form.email || !form.firstname || !form.lastname
              }
            >
              {t('form.send')}
            </SubmitButton>
            {errorMessage ? (
              <dialog ref={rootRef}>
                <ModalAlert
                  open={open}
                  onClose={() => setOpen(false)}
                  container={rootRef}
                >
                  <ErrorMessage
                    errorMessage={errorMessage}
                    errorTitle={t('form.error.title')}
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => setOpen(false)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                  >
                    {t('form.error.sendViaLocalMail')}
                    <SendMailLink href="mailto:julien.moulis@euklei.eu">
                      {t('form.sendAnEmail')}
                    </SendMailLink>
                  </ErrorMessage>
                </ModalAlert>
              </dialog>
            ) : null}
          </FieldGroup>
        </>
      )}
    </Form>
  );
};
